import { useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import ReactMarkdown from "react-markdown";

// Inline markdown (bold/italic/links) without block wrappers.
export const Inline = ({ text }) => <ReactMarkdown allowedElements={["strong", "em", "a", "code"]} unwrapDisallowed components={{ strong: ({ node, ...p }) => <strong className="font-bold text-white" {...p} />, a: ({ node, ...p }) => <a className="text-[var(--accent)] underline" target="_blank" rel="noreferrer" {...p} /> }}>{text || ""}</ReactMarkdown>;

// Collapsible list item used by the guides, manual and handbook.
export const Accordion = ({ id, title, eyebrow, open, onToggle, children }) => (
  <div className={`overflow-hidden rounded-2xl border bg-[#1A1A1A] transition-colors ${open ? "border-[var(--accent)]" : "border-[#333]"}`} data-testid={`accordion-${id}`}>
    <button onClick={onToggle} data-testid={`accordion-toggle-${id}`} className="flex w-full items-center gap-3 p-4 text-left">
      <span className="flex-1">
        {eyebrow && <span className="block text-[10px] font-bold tracking-[0.2em] text-[var(--accent)]">{eyebrow}</span>}
        <span className="block font-semibold">{title}</span>
      </span>
      <ChevronDown size={18} className={`shrink-0 text-neutral-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
    </button>
    {open && <div className="border-t border-[#2a2a2a] px-4 pb-5 pt-4 text-[15px] leading-7 text-neutral-300">{children}</div>}
  </div>
);

export const useSingleOpen = () => {
  const [openId, setOpenId] = useState(null);
  return [openId, (id) => setOpenId((cur) => (cur === id ? null : id))];
};

export const SearchBox = ({ value, onChange, placeholder, testId = "search-input" }) => (
  <label className="flex items-center gap-2 rounded-xl border border-[#333] bg-[#121212] px-4 focus-within:border-[var(--accent)]">
    <Search size={16} className="text-neutral-500" />
    <input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="min-h-[48px] flex-1 bg-transparent text-[15px] text-white outline-none placeholder:text-neutral-500" />
  </label>
);

export const Paragraphs = ({ text }) => (text || "").split(/\n{2,}/).map((p, i) => <p key={i} className="mb-3 whitespace-pre-line last:mb-0"><Inline text={p} /></p>);

export const Bullets = ({ items }) => (
  <ul className="mt-2 space-y-1.5">
    {items.map((b, i) => (
      <li key={i} className="flex gap-2"><span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--accent)]" /><span><Inline text={b} /></span></li>
    ))}
  </ul>
);

export const Tabs = ({ tabs, active, onChange }) => (
  <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1" data-testid="guide-tabs">
    {tabs.map((t) => (
      <button key={t.key} onClick={() => onChange(t.key)} data-testid={`guide-tab-${t.key}`}
        className={`shrink-0 rounded-full border px-4 py-2 text-[11px] font-extrabold tracking-[0.14em] transition-colors ${active === t.key ? "border-[var(--accent)] bg-[var(--accent)] text-black" : "border-[#333] bg-[#1A1A1A] text-neutral-400 hover:text-white"}`}>
        {t.label}
      </button>
    ))}
  </div>
);
